import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "configuration-management"
    }}>{`Configuration management`}</h1>
    <p>{`The `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/packages/buildpack/"
      }}>{`Buildpack`}</a>{` library provides the tools you need to configure your environment and larger, overall workflows that a developer has to configure and control.
These configurations differ across projects and different environments within those projects.
For example, environments for development, testing, staging, and production are configured to support different behaviors.`}</p>
    <h2 {...{
      "id": "the-env-file"
    }}>{`The `}<inlineCode parentName="h2">{`.env`}</inlineCode>{` file`}</h2>
    <p>{`Like the rest of PWA Studio, buildpack uses environment variables as its central source of configuration settings.
A PWA Studio project using buildpack requires a `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/dotenv"
      }}><inlineCode parentName="a">{`.env`}</inlineCode>{` file`}</a>{` in its root directory.
Each line in the file contains a configuration using the following form:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text"
      }}>{`NAME=value
`}</code></pre>
    <p>{`In any script in any programming language, you can access these environment variables directly by sourcing the file as a legal POSIX shell script.`}</p>
    <h2 {...{
      "id": "command-line-interface-cli"
    }}>{`Command Line Interface (CLI)`}</h2>
    <p>{`Buildpack provides a `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/api/buildpack/cli/"
      }}>{`Buildpack CLI`}</a>{` for creating `}<inlineCode parentName="p">{`.env`}</inlineCode>{` files and validating environments.
It also provides library methods for connecting environment management workflows with other tools.`}</p>
    <p>{`Using these provided tools, you can keep global configuration values in a central location and propagate them throughout your project.
This lets you pass common settings down to different library functions without tightly coupling those settings together.`}</p>
    <h2 {...{
      "id": "configuration-management-rationale"
    }}>{`Configuration management rationale`}</h2>
    <p>{`PWA Studio follows the principle that `}<em parentName="p">{`all configuration that `}<strong parentName="em">{`can`}</strong>{` be environment variables, `}<strong parentName="em">{`should`}</strong>{` be environment variables`}</em>{`.`}</p>
    <p>{`Environment variables are portable, cross-platform, and reasonably secure.
They can be individually overridden to give the user a great deal of control over a complex system.
The `}<a parentName="p" {...{
        "href": "https://12factor.net/config"
      }}>{`twelve-factor app`}</a>{` methodology recommends storing config in the environment as its third factor.`}</p>
    <p>{`Many tools use environment variables strictly as edge-case overrides and store their canonical configuration in other formats because
under the strict POSIX definition, environment variables have some limitations:`}</p>
    <ul>
      <li parentName="ul">{`An environment variable name is case insensitive.`}</li>
      <li parentName="ul">{`An environment variable's value can only be a string.`}</li>
      <li parentName="ul">{`Environment variables cannot be nested nor schematized, so they have no built-in data structures.`}</li>
      <li parentName="ul">{`Environment variables all belong to a single namespace, and every running process has access to all of them.`}</li>
    </ul>
    <p>{`These drawbacks are serious enough that some applications use alternate formats, such as:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`XML`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`JSON`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`YAML`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`INI`}</inlineCode>{` / `}<inlineCode parentName="li">{`TOML`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.properties`}</inlineCode>{` files in Java`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`.plist`}</inlineCode>{` files in MacOS`}</li>
      <li parentName="ul">{`PHP associative arrays`}</li>
      <li parentName="ul">{`Apache directives`}</li>
    </ul>
    <p>{`These formats have the following advantages over environment variables:`}</p>
    <ol>
      <li parentName="ol">{`They are a standard human-readable file format`}</li>
      <li parentName="ol">{`They can support nesting and/or namespacing to organize values`}</li>
      <li parentName="ol">{`They support data types and metadata`}</li>
    </ol>
    <p>{`However, none of these formats have `}<em parentName="p">{`won`}</em>{` and become an undisputed replacement for environment variables.
Each one has its own set of quirks and undefined behaviors.
None of them are deeply integrated with OS, shell, and container environments, and
they often do not work consistently across language runtimes.`}</p>
    <p>{`PWA Studio chooses to use environment variables, while providing simple tools for file format, namespacing, and validation.`}</p>
    <p>{`A centralized configurator passes on formatted pieces of the environment to specific tools as parameters, so
these tools do not need to know the specifics of the configuration scheme.
Entry point scripts, such as `}<inlineCode parentName="p">{`server.js`}</inlineCode>{` and `}<inlineCode parentName="p">{`webpack.config.js`}</inlineCode>{`, can use the `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/api/buildpack/cli/load-environment-file/"
      }}>{`loadEnvironment()`}</a>{` tool to deserialize environment variables into any kind of data structure, while storing persistent values in an `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file in the project root directory.`}</p>
    <p>{`Buildpack combines the features of several tools:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.npmjs.com/package/dotenv"
        }}>{`dotenv`}</a>{` for managing environment variables with `}<inlineCode parentName="li">{`.env`}</inlineCode>{` files`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://npmjs.com/package/envalid"
        }}>{`envalid`}</a>{` for describing, validating, and making defaults for settings`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://npmjs.com/package/camelspace"
        }}>{`camelspace`}</a>{` for easily translating configuration between flat environment variables and namespaced objects`}</li>
    </ul>
    <h2 {...{
      "id": "best-practices"
    }}>{`Best practices`}</h2>
    <p>{`The `}<strong parentName="p">{`config rule`}</strong>{` in the `}<a parentName="p" {...{
        "href": "https://12factor.net/config"
      }}>{`twelve-factor app`}</a>{` methodology distinguishes configuration that "does not vary between deploys" from configuration that does.
It requires that configuration that does change between deploys be stored in the environment.
PWA Studio does not make such a distinction.
For config that must never vary, the PWA project maintainer can hardcode that configuration in the entrypoint scripts what use `}<inlineCode parentName="p">{`loadEnvironment()`}</inlineCode>{`.`}</p>
    <p>{`To have environment-variable-based configuration management and enjoy the benefits of file format, namespacing, and validation at the same time, it's important to use `}<inlineCode parentName="p">{`loadEnvironment()`}</inlineCode>{` in a certain way.`}</p>
    <h3 {...{
      "id": "configuration-object"
    }}>{`Configuration object`}</h3>
    <p>{`The purpose of a function such as `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/api/buildpack/cli/load-environment-file/"
      }}>{`loadEnvironment()`}</a>{` is to keep configuration organized without tightly coupling a system to a manager object.
To achieve this, it is important to use `}<inlineCode parentName="p">{`loadEnvironment()`}</inlineCode>{` and the `}<inlineCode parentName="p">{`Configuration`}</inlineCode>{` object it produces at the "top level" or entry point of a program.`}</p>
    <p>{`Avoid passing a `}<inlineCode parentName="p">{`Configuration`}</inlineCode>{` object directly to other tools.
These tools should be usable without `}<inlineCode parentName="p">{`loadEnvironment()`}</inlineCode>{`.
It is always the responsibility of an outer function to pass plain configuration to an inner dependency.`}</p>
    <p>{`Use the `}<inlineCode parentName="p">{`Configuration`}</inlineCode>{` object only when moving between logic layers:`}</p>
    <p><strong parentName="p">{`Bad`}</strong>{`: passing the `}<inlineCode parentName="p">{`Configuration`}</inlineCode>{` object to library methods`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`await PWADevServer.configure(
  {
    publicPath: config.output.publicPath,
    graphqlPlayground: true,
    projectConfig: loadEnvironment(__dirname),
  },
  config
);
`}</code></pre>
    <p>{`The same principle holds when creating your own utilities.`}</p>
    <p><strong parentName="p">{`Bad`}</strong>{`: expecting a `}<inlineCode parentName="p">{`Configuration`}</inlineCode>{` object in a library function`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`class MyWebpackPlugin {
  constructor(config) {
    this.options = config.section("myWebpackPlugin");
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Good`}</strong>{`: passing plain objects created by the Configuration object`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const projectConfig = await loadEnvironment(__dirname);
await PWADevServer.configure(
  {
    publicPath: config.output.publicPath,
    graphqlPlayground: true,
    ...projectConfig.sections("devServer", "imageService", "customOrigin"),
    ...projectConfig.section("magento"),
  },
  config
);
`}</code></pre>
    <h3 {...{
      "id": "naming-convention"
    }}>{`Naming convention`}</h3>
    <p>{`POSIX standard environment variables may not be case sensitive and may not allow very many special characters.`}</p>
    <p>{`The best policy is to use `}<inlineCode parentName="p">{`ALL_CAPS_UNDERSCORE_DELIMITED_ALPHANUMERIC_VARIABLE_NAMES`}</inlineCode>{` when defining environment variables directly.
`}<strong parentName="p">{`Buildpack will ignore any environment variables which do not follow this convention.`}</strong></p>
    <p>{`Buildpack converts between this strict all-caps format (also known as `}<strong parentName="p">{`SCREAMING_SNAKE_CASE`}</strong>{`) and a more convenient JavaScript object which can be nested at any level of delimiter.
When defining new environment variables, make their names long and safely namespace them with prefixes as long as necessary.
`}<inlineCode parentName="p">{`Configuration`}</inlineCode>{` objects have `}<inlineCode parentName="p">{`.section()`}</inlineCode>{` and `}<inlineCode parentName="p">{`.sections()`}</inlineCode>{` methods to create targeted, small JavaScript objects with shorter names.`}</p>
    <h3 {...{
      "id": "fallback"
    }}>{`Fallback`}</h3>
    <p>{`By default, buildpack respects three levels of "fallback" values:`}</p>
    <ol>
      <li parentName="ol">{`Currently declared environment variables, which can be populated on process startup`}</li>
      <li parentName="ol">{`Values from the `}<inlineCode parentName="li">{`.env`}</inlineCode>{` file in the project root`}</li>
      <li parentName="ol">{`Defaults from the metadata in the `}<a parentName="li" {...{
          "href": "/commerce/pwa-studio/api/buildpack/environment/variables/"
        }}>{`Project Environment Definitions`}</a></li>
    </ol>
    <p>{`Additional layers of configuration and on-disk fallback are discouraged.
Inside scripts, environment variables may be combined and merged, but
too much fall-through of project configuration can result in unpredictable and hard-to-maintain runtime configuration.`}</p>
    <h3 {...{
      "id": "project-environment-definitions"
    }}>{`Project environment definitions`}</h3>
    <p>{`All the environment variables expected and/or used by buildpack are defined in `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/pwa-buildpack/envVarDefinitions.json"
      }}>{`packages/pwa-buildpack/envVarDefinitions.json`}</a>{`.`}</p>
    <p>{`This file is used for:`}</p>
    <ul>
      <li parentName="ul">{`Creating a self-documenting `}<inlineCode parentName="li">{`.env`}</inlineCode>{` file`}</li>
      <li parentName="ul">{`Validating environments`}</li>
      <li parentName="ul">{`Deprecating and supporting older settings which have changed`}</li>
    </ul>
    <p>{`If you are contributing to the PWA Studio project and want to add new functionality that should be configured via the environment or change any environment configuration, follow these best practices:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Define any new variables in the `}<inlineCode parentName="p">{`packages/pwa-buildpack/envVarDefinitions.json`}</inlineCode>{` file.
The variable definition object follows the API of `}<a parentName="p" {...{
            "href": "https://npmjs.com/package/envalid"
          }}>{`envalid`}</a>{`, with the addition of a `}<inlineCode parentName="p">{`type`}</inlineCode>{` property indicating the `}<inlineCode parentName="p">{`envalid`}</inlineCode>{` method to use.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Organize variables into named sections in the `}<inlineCode parentName="p">{`sections`}</inlineCode>{` list.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Use the namespacing practices encouraged by `}<a parentName="p" {...{
            "href": "https://npmjs.com/package/camelspace"
          }}>{`camelspace`}</a>{` and `}<inlineCode parentName="p">{`loadEnvironment()`}</inlineCode>{`.`}</p>
        <p parentName="li">{`For example, a new utility `}<inlineCode parentName="p">{`goodStuff()`}</inlineCode>{` might demand environment variables starting with `}<inlineCode parentName="p">{`GOOD_STUFF_`}</inlineCode>{`,
and `}<inlineCode parentName="p">{`packages/pwa-buildpack/envVarDefinitions.json`}</inlineCode>{` might include a new section in its `}<inlineCode parentName="p">{`sections`}</inlineCode>{` list.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`After making any changes to `}<inlineCode parentName="p">{`packages/pwa-buildpack/envVarDefinitions.json`}</inlineCode>{`, record them in the `}<inlineCode parentName="p">{`changes`}</inlineCode>{` list in that file.`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Change entries are objects which include:`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`type`}</inlineCode>{`: `}<strong parentName="li">{`Required.`}</strong>{` The type of the change, either `}<inlineCode parentName="li">{`removed`}</inlineCode>{` or `}<inlineCode parentName="li">{`renamed`}</inlineCode>{`. No other types of change need change entries.`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`name`}</inlineCode>{`: `}<strong parentName="li">{`Required.`}</strong>{` The affected environment variable name.`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`dateChanged`}</inlineCode>{`: `}<strong parentName="li">{`Required.`}</strong>{` The date the change entry was added, in a format parseable by the JavaScript `}<inlineCode parentName="li">{`Date`}</inlineCode>{` constructor.`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`warnForDays`}</inlineCode>{`: `}<strong parentName="li">{`Optional`}</strong>{`, default `}<inlineCode parentName="li">{`180`}</inlineCode>{`. A number of days that the warning should keep logging on every run, counting from the `}<inlineCode parentName="li">{`dateChanged`}</inlineCode>{`. The default `}<em parentName="li">{`and`}</em>{` maximum is 180 days, so use this property only if you want the change to log for a `}<em parentName="li">{`shorter`}</em>{` time than default. This prevents an old, out-of-date warning message from cluttering logs long after the user no longer needs to see it.`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`removed`}</inlineCode>{` entries should include a human-readable `}<inlineCode parentName="li">{`reason`}</inlineCode>{`.
`}<strong parentName="li">{`After removing a variable definition, leave the `}<inlineCode parentName="strong">{`removed`}</inlineCode>{` entry permanently`}</strong>{` to log an error if the old variable is found, encouraging out-of-date installations to upgrade.`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`renamed`}</inlineCode>{` entries should include the old name as `}<inlineCode parentName="li">{`name`}</inlineCode>{`, and the new name as `}<inlineCode parentName="li">{`updated`}</inlineCode>{`.
They must also include a `}<inlineCode parentName="li">{`supportLegacy`}</inlineCode>{` boolean.
If this is `}<inlineCode parentName="li">{`true`}</inlineCode>{`, then `}<inlineCode parentName="li">{`loadEnvironment()`}</inlineCode>{` will continue to support the old value while logging a warning, until either the new variable name has a value, or the change entry expires.`}</li>
        </ul>
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      